/**
 * CoachPlaybook - Sport Context Provider
 * Version: 0.6.0
 * Description: React Context for sport/age-group state with IndexedDB persistence
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSetting, setSetting } from '../../utils/db';
import { getSport, getDefaultSport } from '../../sports';

const SportContext = createContext(null);

const SPORT_SETTING_KEY = 'selectedSport';
const AGE_GROUP_SETTING_KEY = 'selectedAgeGroup';

export const SportProvider = ({ children }) => {
  const [currentSport, setCurrentSport] = useState('basketball');
  const [currentAgeGroup, setCurrentAgeGroup] = useState(null);
  const [sportConfig, setSportConfig] = useState(getDefaultSport());
  const [loaded, setLoaded] = useState(false);

  // Load persisted selection on mount
  useEffect(() => {
    const loadPersistedSport = async () => {
      try {
        const savedSport = await getSetting(SPORT_SETTING_KEY, 'basketball');
        const savedAgeGroup = await getSetting(AGE_GROUP_SETTING_KEY, null);

        const config = getSport(savedSport) || getDefaultSport();
        setCurrentSport(config.id);
        setSportConfig(config);

        // Validate saved age group still exists in sport config
        if (savedAgeGroup && config.ageGroups?.some(ag => ag.id === savedAgeGroup)) {
          setCurrentAgeGroup(savedAgeGroup);
        } else if (config.ageGroups?.length > 0) {
          setCurrentAgeGroup(config.ageGroups[0].id);
        }
      } catch (err) {
        console.error('SportContext: failed to load persisted sport', err);
        const fallback = getDefaultSport();
        setCurrentSport(fallback.id);
        setSportConfig(fallback);
        if (fallback.ageGroups?.length > 0) {
          setCurrentAgeGroup(fallback.ageGroups[0].id);
        }
      } finally {
        setLoaded(true);
      }
    };
    loadPersistedSport();
  }, []);

  const setSport = useCallback(async (sportId) => {
    const config = getSport(sportId);
    if (!config) return;

    setCurrentSport(sportId);
    setSportConfig(config);

    // Reset age group to first available
    const firstAgeGroup = config.ageGroups?.[0]?.id || null;
    setCurrentAgeGroup(firstAgeGroup);

    await setSetting(SPORT_SETTING_KEY, sportId);
    await setSetting(AGE_GROUP_SETTING_KEY, firstAgeGroup);
  }, []);

  const setAgeGroup = useCallback(async (ageGroupId) => {
    // Validate against current sport config
    if (sportConfig.ageGroups?.some(ag => ag.id === ageGroupId)) {
      setCurrentAgeGroup(ageGroupId);
      await setSetting(AGE_GROUP_SETTING_KEY, ageGroupId);
    }
  }, [sportConfig]);

  const value = {
    currentSport,
    currentAgeGroup,
    sportConfig,
    setSport,
    setAgeGroup,
    loaded
  };

  return (
    <SportContext.Provider value={value}>
      {children}
    </SportContext.Provider>
  );
};

export const useSport = () => {
  const context = useContext(SportContext);
  if (!context) {
    throw new Error('useSport must be used within a SportProvider');
  }
  return context;
};

export default SportContext;
